import { useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const checkAuth = useAuth()?.checkAuth;

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  const handleVerify = useCallback(
    async (verificationCode) => {
      if (verificationCode.length !== 6) return;
      setSubmitting(true);
      try {
        const res = await api.post("/client/verify", { email, code: verificationCode });
        if (res?.data?.access_token) {
          // Same session storage as AuthContext.login
          localStorage.setItem("token", res.data.access_token);
          if (res.data.csrf_token) localStorage.setItem("csrf_token", res.data.csrf_token);
          await checkAuth?.();
          toast.success("Email verified! Welcome to Elementar.");
          navigate("/home");
        } else {
          toast.success("Email verified! You can now sign in.");
          navigate("/login");
        }
      } catch (err) {
        const msg = err.response?.data?.detail || "Verification failed. Please try again.";
        toast.error(msg);
        setCode("");
      } finally {
        setSubmitting(false);
      }
    },
    [email, navigate, checkAuth]
  );

  const handleCodeChange = (value) => {
    setCode(value);
    if (value.length === 6) {
      handleVerify(value);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await api.post("/client/resend-code", { email });
      toast.success("Verification code resent! Check your email.");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to resend code. Please try again.";
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      {/* Left brand panel */}
      <div
        style={{
          flex: "0 0 55%",
          backgroundColor: "#C41E1E",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "3rem",
          position: "relative",
        }}
        className="hidden md:flex"
      >
        <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "3rem", color: "#FFFFFF", letterSpacing: "6px", marginBottom: "1rem" }}>
          ELEMENTAR
        </h1>
        <div style={{ width: "80px", height: "1px", backgroundColor: "rgba(255,255,255,0.4)", marginBottom: "1.5rem" }} />
        <p style={{ fontFamily: "'Cormorant Garamond', Garamond, serif", fontWeight: 400, fontSize: "1.15rem", color: "rgba(255,255,255,0.85)", letterSpacing: "3px", textTransform: "uppercase", textAlign: "center" }}>
          Unified alternative investment intelligence
        </p>
        <p style={{ position: "absolute", bottom: "2rem", fontFamily: "'DM Sans', sans-serif", fontSize: "0.7rem", letterSpacing: "3px", color: "rgba(255,255,255,0.4)", textTransform: "uppercase" }}>
          From Wooden Owl AG
        </p>
      </div>

      {/* Right form panel */}
      <div style={{ flex: 1, backgroundColor: "#0A0A0A", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
        <div style={{ width: "100%", maxWidth: "380px", textAlign: "center" }}>
          {/* Mobile logo */}
          <div className="md:hidden" style={{ marginBottom: "2rem" }}>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "1.8rem", color: "#C41E1E", letterSpacing: "4px" }}>
              ELEMENTAR
            </h2>
          </div>

          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 400, fontSize: "1.75rem", color: "#F0EDE8", marginBottom: "0.5rem" }}>
            Check your email
          </h2>
          <p style={{ fontFamily: "'Cormorant Garamond', Garamond, serif", fontSize: "1rem", color: "#8A8A8A", marginBottom: "2.5rem" }}>
            {email ? (
              <>We sent a 6-digit code to <span style={{ color: "#F0EDE8" }}>{email}</span></>
            ) : (
              "Enter the 6-digit verification code from your email"
            )}
          </p>

          <div style={{ display: "flex", justifyContent: "center", marginBottom: "2rem" }}>
            <InputOTP maxLength={6} value={code} onChange={handleCodeChange} disabled={submitting}>
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          {submitting && (
            <div style={{ marginBottom: "1.5rem" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", fontSize: "0.85rem", color: "#8A8A8A" }}>
                <span className="animate-spin rounded-full h-4 w-4 border-2" style={{ borderColor: "rgba(255,255,255,0.15)", borderTopColor: "#C41E1E" }} />
                Setting up your mailbox…
              </span>
            </div>
          )}

          <div style={{ marginBottom: "1.5rem" }}>
            <p style={{ fontSize: "0.85rem", color: "#555", marginBottom: "0.5rem" }}>
              Didn't receive the code?
            </p>
            <button
              onClick={handleResend}
              disabled={resending}
              style={{
                background: "none", border: "none", cursor: resending ? "not-allowed" : "pointer",
                color: "#C41E1E", fontSize: "0.85rem", fontWeight: 500,
                fontFamily: "'DM Sans', sans-serif", borderBottom: "1px solid rgba(196,30,30,0.3)",
                padding: 0, opacity: resending ? 0.6 : 1,
              }}
            >
              {resending ? "Resending..." : "Resend Code"}
            </button>
          </div>

          <button
            onClick={() => navigate("/login")}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#8A8A8A", fontSize: "0.85rem",
              fontFamily: "'DM Sans', sans-serif",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#F0EDE8")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#8A8A8A")}
          >
            Back to Sign In
          </button>

          <div style={{ marginTop: "3rem" }}>
            <p style={{ fontSize: "0.7rem", color: "#555", letterSpacing: "1px" }}>🇨🇭 Swiss-domiciled</p>
            <p style={{ fontSize: "0.65rem", color: "#444", marginTop: "0.35rem" }}>© 2026 Elementar</p>
          </div>
        </div>
      </div>
    </div>
  );
}
